import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { FolderOpen, History } from '@mui/icons-material';
import { AuditEvent, platformApi, Project } from '../services/platformApi';
import { formatTranslatedLabel } from '../i18n';
import { getStatusColor } from './projectConstants';
import { useLanguage } from '../LanguageContext';

const AuditDashboard: React.FC = () => {
  const { locale, t } = useLanguage();
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [error, setError] = useState<string | null>(null);

  const loadProjects = useCallback(async () => {
    try {
      setError(null);
      const projectData = await platformApi.listProjects();
      setProjects(projectData);
      setSelectedProjectId((current) => current || projectData[0]?.id.toString() || '');
    } catch (err) {
      setError(t('couldNotLoadProjects'));
    }
  }, [t]);

  const loadAuditEvents = useCallback(async (projectId: string) => {
    if (!projectId) {
      setAuditEvents([]);
      return;
    }

    try {
      setError(null);
      setAuditEvents(await platformApi.listProjectAuditEvents(Number(projectId)));
    } catch (err) {
      setError(t('couldNotLoadAuditEvents'));
    }
  }, [t]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    loadAuditEvents(selectedProjectId);
  }, [loadAuditEvents, selectedProjectId]);

  const selectedProject = projects.find((project) => project.id.toString() === selectedProjectId);

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('auditTrail')}
          </Typography>
          <Typography color="text.secondary">
            {t('auditTrailDescription')}
          </Typography>
        </Box>
        {selectedProject && (
          <Button
            component={RouterLink}
            to={`/projects/${selectedProject.id}`}
            startIcon={<FolderOpen />}
            variant="outlined"
          >
            {t('openProject')}
          </Button>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <History />
          <FormControl sx={{ minWidth: 320 }}>
            <InputLabel id="project-audit-label">{t('project')}</InputLabel>
            <Select
              labelId="project-audit-label"
              label={t('project')}
              value={selectedProjectId}
              onChange={(event) => setSelectedProjectId(event.target.value)}
            >
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id.toString()}>
                  {project.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedProject && (
            <Chip
              size="small"
              label={formatTranslatedLabel(t, selectedProject.status)}
              color={getStatusColor(selectedProject.status)}
            />
          )}
        </Stack>
      </Paper>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('time')}</TableCell>
              <TableCell>{t('action')}</TableCell>
              <TableCell>{t('target')}</TableCell>
              <TableCell>{t('actor')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {auditEvents.map((event) => (
              <TableRow key={event.id} hover>
                <TableCell>
                  {event.created_at ? new Date(event.created_at).toLocaleString(locale) : t('notRecorded')}
                </TableCell>
                <TableCell>{event.action}</TableCell>
                <TableCell>
                  {event.target_type}
                  {event.target_id ? ` #${event.target_id}` : ''}
                </TableCell>
                <TableCell>{event.actor_user_id ? `${t('user')} #${event.actor_user_id}` : event.actor_type}</TableCell>
              </TableRow>
            ))}
            {auditEvents.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography color="text.secondary" align="center" sx={{ py: 5 }}>
                    {t('noAuditEventsForProject')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AuditDashboard;
